/**
 * Collection of parsing functions for the GUI front end. This is typically
 * to for item look ups as well as common formatting/styling methods
 * */
import { notify } from "./notificationService";
import Icons from "../components/common/Icons";

export function isDef(obj) {
  return typeof obj !== "undefined";
}

export function validResponse(data) {
  return data && typeof data == "object";
}

export function notifyResult({ data, success, error }, type = "object") {
  let isValid = data && data !== "";
  if (type === "object") {
    isValid = validResponse(data);
  }
  isValid
    ? notify(success, { type: "success" })
    : notify(error, { type: "error" });
  return isValid;
}

export function notifyResultWithTime(
  { data, success, error },
  type = "object",
) {
  let isValid = data && data !== "";
  if (type === "object") {
    isValid = validResponse(data);
  }
  isValid
    ? notify(success, { type: "success", autoClose: 2000 })
    : notify(error, { type: "error", autoClose: false });
  return isValid;
}

// Casting date object to a standard string notation
export const dateYYYYMMMDD = (date) => {
  if (date === null) return "--";
  if (date === undefined) date = new Date();
  if (!(date instanceof Date)) date = new Date(Date.parse(date));
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const __month__ = {
    1: "Jan",
    2: "Feb",
    3: "Mar",
    4: "Apr",
    5: "May",
    6: "Jun",
    7: "Jul",
    8: "Aug",
    9: "Sep",
    10: "Oct",
    11: "Nov",
    12: "Dec",
  };
  return `${year}.${__month__[month]}.${day}`;
};

export const dateYYYYMMMDDHHMM = (date) => {
  if (date === null) return "";
  if (date === undefined) date = new Date();
  if (!(date instanceof Date)) date = new Date(Date.parse(date));
  const yyyy_mmm_dd = dateYYYYMMMDD(date);
  const hour = date.getHours();
  const min = date.getMinutes();
  const time_str = `${String(hour).padStart(2, 0)}:${String(min).padStart(2, 0)}`;
  return `${yyyy_mmm_dd}-${time_str}`;
};

export const boolToIcon = (value) => {
  return value === null || value === undefined
    ? Icons.empty
    : value === true
      ? Icons.ok
      : Icons.wrong;
};

function _firstThursday(year) {
  const firstThursday = new Date(year, 0, 1);
  firstThursday.setDate(
    firstThursday.getDay() === 4
      ? firstThursday.getDate()
      : firstThursday.getDate() + ((4 - firstThursday.getDay() + 7) % 7),
  );
  return firstThursday;
}

export function getISOWeek(date) {
  const day_ms = 24 * 60 * 60 * 1000;
  const d = new Date(date);
  const firstThursday = _firstThursday(d.getFullYear());
  const opYear =
    d - firstThursday < -(4 * day_ms) ? d.getFullYear() - 1 : d.getFullYear();
  const opThursday = _firstThursday(opYear);
  return {
    year: opYear,
    week: Math.floor((d - opThursday + 4 * day_ms) / (7 * day_ms)) + 1,
  };
}

export function getWeekDates(year, weekNumber) {
  const firstThursday = _firstThursday(year);
  const firstMonday = new Date(firstThursday);
  firstMonday.setDate(firstThursday.getDate() - 3);
  const startOfWeek = new Date(firstMonday);
  startOfWeek.setDate(firstMonday.getDate() + (weekNumber - 1) * 7);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  return { start: startOfWeek, end: endOfWeek };
}

export function getPrettyBkgColor(value) {
  if (value === "" || value === null || value === undefined) return "";
  if (value === "green") return "#AAFFAA";
  if (value === "red") return "#FFAAAA";
  if (value === "orange") return "#FFCCAA";
}

// Rendering as a string if the value is defined and not null, otherwise render as --
export function renderIfNotEmpty(value) {
  if (
    value === "" ||
    value === null ||
    value === undefined ||
    value === "empty"
  )
    return "--";
  return value;
}

export function createBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

// Given a list of baseplate extracted from the central database, find the
// entry in the database via an "identifier" string. For the identifier, we will
// try the CMS bar_code if the identifier starts with 320BA, the CMS
// bare_barcode if it starts with 320BP, the workshop_serial if it does not fit
// the 2 patterns.
//
// Return "undefined" if not found
export function findBasePlateByIdentifier(bp_list, identifier) {
  if (typeof identifier == "string" && identifier.startsWith("320BA")) {
    return bp_list.find((bp) => bp.bar_code === identifier);
  } else if (typeof identifier == "string" && identifier.startsWith("320BP")) {
    return bp_list.find((bp) => bp.bare_barcode === identifier);
  } else {
    return bp_list.find(
      (bp) => Number(bp.workshop_serial) === Number(identifier),
    );
  }
}

// Common methods for validating input. For each input, we will allow the
// component function to define whether the item is allowed to be empty or not.
// Return an empty string if input is considered valid

export function nonEmptyWrap(input, required, func) {
  if (input === "" || input === null) {
    if (required) return "Cannot be empty.";
    else return "";
  } else return func(input);
}

function _validateAlphaNumeric(input) {
  const alphanumericRegex = /^[a-zA-Z0-9-]+$/;
  return alphanumericRegex.test(input) ? "" : "Must be an alphanumeric value";
}

export function validateAlphanumeric(required = false) {
  return (input) => nonEmptyWrap(input, required, _validateAlphaNumeric);
}

function _validateDecimal(input) {
  const decimalRegex = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;
  return decimalRegex.test(input) ? "" : "Must be a decimal value";
}

export function validateDecimal(required = false) {
  return (input) => nonEmptyWrap(input, required, _validateDecimal);
}

function _validatePositiveInt(input) {
  const number = Number(input);
  return Number.isInteger(number) && number > 0
    ? ""
    : "Must be a positive integer";
}

export function validatePositiveInt(required = false) {
  return (input) => nonEmptyWrap(input, required, _validatePositiveInt);
}

function _validateNonNegativeInt(input) {
  const number = Number(input);
  return Number.isInteger(number) && number >= 0
    ? ""
    : "Must be a non-negative integer";
}

export function validateNonNegativeInt(required = false) {
  return (input) => nonEmptyWrap(input, required, _validateNonNegativeInt);
}

export function noCheck() {
  return "";
}
